import { useMutation, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { delContactAddress } from '../../../api/DELETE/delContactAddress'
import { createContactAddress } from '../../../api/POST/contactAddress'
import { putContactAddress } from '../../../api/PUT/updateContactAddress'
import { putContactAddressFavorite } from '../../../api/PUT/updateContactAddressFavorite'
import { selectRole, toggleCsrfState } from '../../../slices/authSlice'
import {
  addAllContactAddress,
  addContactAddress,
  categoryFilter,
  decrementCountFavorite,
  editContactAddress,
  incrementCountFavorite,
  removeAllContactAddress,
  removeContactAddress,
  resetAllContactAddress,
  resetContactAddress,
  selectContactFilterType,
  sortContact,
  toggleFavorite,
} from '../../../slices/contactAddressSlice'
import useNavigatePrevPage from '../../useNavigatePrevPage'
import { Category } from '../../../common/ContactAddress.type'

declare global {
  // sessionExpirationDetected を redux authSlice に保持した場合、useMutateContactAddress での更新更新をuseMutateAuthで検出できないため、globalに保持する
  var sessionExpirationDetected: boolean;   // セッションタイムアウトフラグ
}

export const useMutateContactAddress = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const queryClient = useQueryClient()

  const { navigatePrevPage } = useNavigatePrevPage()
  const loggedinUserRole = useSelector(selectRole)
  const contactFilterType = useSelector(selectContactFilterType)

  const createContactAddressMutation = useMutation(createContactAddress, {
    onSuccess: (data) => {
      const { data: contactAddress } = data
      dispatch(addContactAddress(contactAddress))
      dispatch(addAllContactAddress(contactAddress))
      dispatch(categoryFilter((contactFilterType === "" ? (loggedinUserRole !== 4 ? 'all' : 'personal') : contactFilterType as Category)))
      dispatch(sortContact({ sortOption: 'lastNameKana', orderAsc: true }))
      queryClient.setQueryData('contactAddresses', (old: any) => {
        if (!old) return [contactAddress]
        if (!Array.isArray(old)) return old
        return [...old, contactAddress]
      })
      navigatePrevPage()
    },
    onError: (err: any) => {
      if (err.response.status === 401) {
        // console.log(`useMutateContactAddress() err=${err}`)
        dispatch(resetAllContactAddress([]))
        dispatch(resetContactAddress([]))
        if (global.sessionExpirationDetected !== true) {
          alert('セッションが切れています。再度ログインしなおしてください。')
        }
        global.sessionExpirationDetected = true;
        navigate('/login')
      }
      if (err.response.status === 404) alert(`連絡先の登録に失敗しました`)
      if (err.response.data.detail === 'The CSRF token has expired.') {
        dispatch(toggleCsrfState())
      }
    },
  })

  const updateContactAddressMutation = useMutation(putContactAddress, {
    onSuccess: (data) => {
      const { data: contactAddress } = data
      dispatch(editContactAddress(contactAddress))
      dispatch(sortContact({ sortOption: 'lastNameKana', orderAsc: true }))
      queryClient.setQueryData('contactAddresses', (old: any) => {
        if (!old || !Array.isArray(old)) return old
        return old.map((row: any) => (row.id === contactAddress.id ? contactAddress : row))
      })
      navigatePrevPage()
    },
    onError: (err: any) => {
      if (err.response.status === 401) {
        alert('セッションが切れています。再度ログインしなおしてください。')
        navigate('/login')
      }
      if (err.response.status === 404) alert(`連絡先の更新に失敗しました`)
      if (err.response.data.detail === 'The CSRF token has expired.') {
        dispatch(toggleCsrfState())
      }
    },
  })

  const updateContactAddressFavoriteMutation = useMutation(putContactAddressFavorite, {
    onSuccess: (_, variables) => {
      const { id, isFavorite } = variables
      dispatch(toggleFavorite(id))
      dispatch(
        isFavorite ? incrementCountFavorite() : decrementCountFavorite(),
      )
      navigatePrevPage()
    },
    onError: (err: any) => {
      if (err.response.status === 401) {
        alert('セッションが切れています。再度ログインしなおしてください。')
        navigate('/login')
      }
      if (err.response.status === 404) alert(`連絡先のお気に入り更新に失敗しました`)
      if (err.response.data.detail === 'The CSRF token has expired.') {
        dispatch(toggleCsrfState())
      }
    },
  })

  const deleteContactAddressMutation = useMutation(delContactAddress, {
    onSuccess: (_, variables) => {
      const id = variables
      dispatch(removeContactAddress(id))
      dispatch(removeAllContactAddress(id))
      dispatch(sortContact({ sortOption: 'lastNameKana', orderAsc: true }))
      navigatePrevPage()
    },
    onError: (err: any) => {
      if (err.response.status === 401) {
        alert('セッションが切れています。再度ログインしなおしてください。')
        navigate('/login')
      }

       /**
       * 修正ロジック
       */
      if (err.response.status === 409){
        alert(err.response.data.detail)
      }

      /**
       * 既存ロジック
       */
      // if (err.response.status === 409)
      //   alert(
      //     'このユーザーは、グループ管理者のため削除が行えません。先に該当グループの管理者を変更するか、該当グループの削除を行ってください。',
      //   )
      if (err.response.status === 404) alert(`連絡先の削除に失敗しました`)
      if (err.response.data.detail === 'The CSRF token has expired.') {
        dispatch(toggleCsrfState())
      }
    },
  })

  return {
    createContactAddressMutation,
    updateContactAddressMutation,
    updateContactAddressFavoriteMutation,
    deleteContactAddressMutation,
  }
}
